import { Column } from "primereact/column";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Skeleton } from "antd";
import React, { useEffect, useState } from "react";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { tableBody, tableHeader, tableName } from "../../../styles/TableStyles";
import { ActionBodyText } from "../styles";
import { usePostedReportedCrime } from "../../../hooks/useCrimes";
import Colors from "../../../config/Colors";
import CustomInputField from "../../../components/widgets/Inputs/CustomInputField";
import { SearchOutlined } from "@ant-design/icons";

const PostedPage = () => {
  const navigate = useHistory();
  const navigateTo = (category: string) => {
    navigate.push(`/home/posted-reported-crimes-category/${category}`);
  };

  const { data, isLoading, isFetching, refetch } = usePostedReportedCrime();

  const customInputTheme = {
    token: {
      colorPrimaryHover: `${Colors.appMainColor}`,
    },
  };

  const [filters, setFilters] = useState<DataTableFilterMeta | undefined>(
    undefined
  );

  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      lastupdated: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
      },
      category: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
      },
    });
    setGlobalFilterValue("");
  };

  const clearFilter = () => {
    initFilters();
  };

  const onGlobalFilterChangeV2 = (e: string) => {
    const _filters = { ...filters };
    // @ts-ignore
    _filters.global.value = e;

    setFilters(_filters);
    setGlobalFilterValue(e);
  };

  useEffect(() => {
    initFilters();
  }, []);

  useEffect(() => {
    refetch();
  }, []);

  const dateBodyTemplate = (rowData: ITabItemReportedCrime) => {
    return moment(rowData.lastupdated).format("YYYY/MM/DD");
  };
  const categoryBodyTemplate = (rowData: ITabItemReportedCrime) => {
    return rowData.category;
  };

  const viewMoreBodyTemplate = (rowData: ITabItemReportedCrime) => {
    const category = rowData.category;
    return (
      <ActionBodyText
        onClick={() => {
          navigateTo(category);
        }}
        className="cursor-pointer"
      >
        View More
      </ActionBodyText>
    );
  };

  // Skeleton component for table rows
  const TableRowSkeleton = () => (
    <div className="flex items-center justify-between p-4 border-b border-gray-100">
      <div className="flex-1 pr-4">
        <Skeleton.Input style={{ width: 120, height: 20 }} active />
      </div>
      <div className="flex-1 pr-4">
        <Skeleton.Input style={{ width: 80, height: 20 }} active />
      </div>
      <div className="flex-1 pr-4">
        <Skeleton.Input style={{ width: 150, height: 20 }} active />
      </div>
      <div className="flex-1">
        <Skeleton.Button style={{ width: 80, height: 20 }} active />
      </div>
    </div>
  );

  // Table header skeleton
  const TableHeaderSkeleton = () => (
    <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
      <div className="flex-1 pr-4">
        <Skeleton.Input style={{ width: 100, height: 16 }} active />
      </div>
      <div className="flex-1 pr-4">
        <Skeleton.Input style={{ width: 60, height: 16 }} active />
      </div>
      <div className="flex-1 pr-4">
        <Skeleton.Input style={{ width: 120, height: 16 }} active />
      </div>
      <div className="flex-1">
        <Skeleton.Input style={{ width: 40, height: 16 }} active />
      </div>
    </div>
  );

  // Complete table skeleton
  const TableSkeleton = () => (
    <div
      className="w-full border border-gray-200 rounded-lg overflow-hidden"
      style={{ minWidth: "50rem" }}
    >
      <TableHeaderSkeleton />
      {Array.from({ length: 10 }).map((_, index) => (
        <TableRowSkeleton key={`skeleton-row-${index}`} />
      ))}
      {/* Pagination skeleton */}
      <div className="flex items-center justify-between p-4 bg-gray-50 border-t border-gray-200">
        <div className="flex gap-2">
          <Skeleton.Button style={{ width: 30, height: 30 }} active />
          <Skeleton.Button style={{ width: 30, height: 30 }} active />
          <Skeleton.Button style={{ width: 30, height: 30 }} active />
          <Skeleton.Button style={{ width: 30, height: 30 }} active />
        </div>
        <Skeleton.Input style={{ width: 200, height: 20 }} active />
        <Skeleton.Input style={{ width: 100, height: 32 }} active />
      </div>
    </div>
  );

  return (
    <div className="px-3">
      <div className="flex flex-row gap-1 justify-between mb-2 w-full">
        <div className="flex flex-row align-items-center" style={tableName}>
          Reported Crimes
        </div>
        <CustomInputField
          value={globalFilterValue}
          onChange={(e: string) => onGlobalFilterChangeV2(e)}
          onPressEnter={() => {}}
          theme={customInputTheme}
          inputProps={{
            size: "large",
            placeholder: "Search",
            allowClear: true,
            className: "ml-2 w-3",
            prefix: <SearchOutlined />,
          }}
          mergeThemes
        />
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : (
        <DataTable
          value={data}
          paginator
          rows={10}
          filters={filters}
          globalFilterFields={["lastupdated", "category"]}
          rowsPerPageOptions={[10, 25, 50, 100]}
          tableStyle={{ minWidth: "50rem" }}
          sortMode="multiple"
          emptyMessage="No posted reported crimes found."
          scrollable
          scrollHeight="40rem"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Operators"
          stateStorage="session"
          stateKey={`dt-state-operators-posted-table`}
        >
          <Column
            header="Last Updated"
            headerStyle={tableHeader}
            style={{ background: "#FBFBFB" }}
            bodyStyle={tableBody}
            body={dateBodyTemplate}
          />
          <Column
            field="numbers"
            header="Numbers"
            headerStyle={tableHeader}
            style={{ background: "#FBFBFB" }}
            bodyStyle={tableBody}
          />
          <Column
            header="Category of Crime"
            headerStyle={tableHeader}
            style={{ background: "#FBFBFB" }}
            bodyStyle={tableBody}
            body={categoryBodyTemplate}
          />
          <Column
            header=""
            headerStyle={tableHeader}
            style={{ background: "#FBFBFB" }}
            bodyStyle={tableBody}
            body={viewMoreBodyTemplate}
          />
        </DataTable>
      )}
    </div>
  );
};

export default PostedPage;
